import type {
	PlausibilityPrior,
	VerificationScopeType,
} from "@sitescene/types";

import { type Column, DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import {
	usePriorMutations,
	useTenantPriors,
} from "@/features/plausibility/use-priors";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";

import { priorColumns } from "./prior-columns";
import { PriorForm } from "./prior-form";
import { usePriorRowEdit } from "./use-prior-row-edit";

/**
 * Scoped prior odds of the selected organization: overrides of the global
 * baseline for one installation, zone, or sensor. Overrides are sparse on
 * purpose: write one only when a real difference has been observed or is
 * confidently expected.
 */
export const TenantPriors = () => {
	const taxonomy = useTaxonomy();
	const classes = (taxonomy.data ?? []).filter((cls) => cls.alertable);
	const priors = useTenantPriors();
	const { deleteTenant, upsertTenant } = usePriorMutations();
	const busy = deleteTenant.isPending || upsertTenant.isPending;
	const rows = priors.data ?? [];
	const { edit, startEdit } = usePriorRowEdit((row, input) => {
		if (row.scopeType === "global" || !row.scopeRef) {
			return Promise.reject(new Error("Row has no scope"));
		}
		return upsertTenant.mutateAsync({
			...input,
			scopeRef: row.scopeRef,
			scopeType: row.scopeType as Exclude<VerificationScopeType, "global">,
		});
	});
	const columns: Column<PlausibilityPrior>[] = [
		{ header: "Scope", key: "scope", render: (row) => row.scopeType },
		{
			header: "Ref",
			key: "ref",
			render: (row) => <code className="text-xs">{row.scopeRef}</code>,
		},
		...priorColumns({
			busy,
			edit,
			onDelete: (id) => deleteTenant.mutate(id),
			onEdit: startEdit,
		}),
	];

	return (
		<section>
			<div>
				<PriorForm
					busy={busy}
					classes={classes}
					error={edit ? null : upsertTenant.error}
					onSubmit={(input) => upsertTenant.mutate(input)}
					scoped
				/>
			</div>
			<div className="mt-4">
				<StatusMessage
					empty="No scoped priors. Every scope inherits the global baseline."
					error={priors.error}
					isEmpty={!priors.isLoading && rows.length === 0}
					isLoading={priors.isLoading}
					loading="Loading scoped priors…"
				/>
				{edit && upsertTenant.error && (
					<p className="mb-2 text-sm text-error-primary" role="alert">
						Could not save. {upsertTenant.error.message}
					</p>
				)}
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(row) => row.id} rows={rows} />
				)}
			</div>
		</section>
	);
};
